import { useState, useEffect } from "react";

const navLinks = [
  { label: "About", href: "#about" },
  { label: "Work", href: "#work" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Music", href: "#music" },
  { label: "Connect", href: "#contact" },
];

// Smoothly scrolls to the target section, accounting for the fixed header height
function scrollToSection(href) {
  const id = href.replace("#", "");
  const el = document.getElementById(id);
  if (!el) return;

  const headerHeight =
    document.getElementById("app-header")?.offsetHeight ?? 72;
  const top = el.getBoundingClientRect().top + window.scrollY - headerHeight;

  window.scrollTo({ top, behavior: "smooth" });
}

export const AppHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActive] = useState("");

  // Add shadow once user scrolls
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on scroll
  useEffect(() => {
    const close = () => {
      if (menuOpen) setMenuOpen(false);
    };
    window.addEventListener("scroll", close, { passive: true });
    return () => window.removeEventListener("scroll", close);
  }, [menuOpen]);

  // Highlight the active section as user scrolls
  useEffect(() => {
    const ids = navLinks.map((l) => l.href.replace("#", ""));
    const headerHeight =
      document.getElementById("app-header")?.offsetHeight ?? 72;

    const onScroll = () => {
      let current = "";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - headerHeight - 16 <= 0) {
          current = id;
        }
      }
      setActive(current);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // run once on mount
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  function handleLinkClick(href) {
    setMenuOpen(false);
    scrollToSection(href);
  }

  return (
    <header
      id="app-header"
      className={`fixed top-0 left-0 right-0 z-50 bg-cream transition-shadow duration-300 ${
        scrolled ? "shadow-sm" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-16 py-4 flex items-center justify-between">
        {/* ── Brand ── */}
        <button
          onClick={() => scrollToSection("#home")}
          className="font-cormorant text-ink text-xl font-light tracking-wide hover:text-earth transition-colors duration-300"
          aria-label="Back to top"
        >
          Noah Fernandes
        </button>

        {/* ── Mobile hamburger ── */}
        <button
          className="sm:hidden text-stone hover:text-ink transition-colors duration-200 focus:outline-none p-1"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
        >
          {menuOpen ? (
            // Close icon
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            // Hamburger icon
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5"
              />
            </svg>
          )}
        </button>

        {/* ── Desktop nav ── */}
        <nav
          className="hidden sm:flex items-center gap-1"
          aria-label="Main navigation"
        >
          {navLinks.map((link) => {
            const isActive = activeSection === link.href.replace("#", "");
            return (
              <button
                key={link.href}
                onClick={() => handleLinkClick(link.href)}
                className={`
                  font-jost font-light text-[0.72rem] tracking-[0.18em] uppercase
                  px-3 py-2 transition-colors duration-200
                  ${isActive ? "text-ink" : "text-stone hover:text-ink"}
                `}
              >
                {link.label}
              </button>
            );
          })}

          {/* Book CTA */}
          <button
            onClick={() => handleLinkClick("#contact")}
            className="ml-4 px-5 py-2 border border-earth text-earth font-jost font-light text-[0.72rem] tracking-[0.18em] uppercase hover:bg-earth hover:text-cream transition-all duration-300"
          >
            Book a session
          </button>
        </nav>
      </div>

      {/* ── Mobile dropdown menu ── */}
      <div
        className={`
          sm:hidden overflow-hidden transition-all duration-300 ease-in-out
          ${menuOpen ? "max-h-96 border-t border-sand-dark" : "max-h-0"}
        `}
        aria-hidden={!menuOpen}
      >
        <nav
          className="bg-cream px-6 py-4 flex flex-col gap-1"
          aria-label="Mobile navigation"
        >
          {navLinks.map((link) => {
            const isActive = activeSection === link.href.replace("#", "");
            return (
              <button
                key={link.href}
                onClick={() => handleLinkClick(link.href)}
                className={`
                  w-full text-left font-jost font-light text-[0.78rem] tracking-[0.18em] uppercase
                  py-3 border-b border-sand-dark last:border-b-0 transition-colors duration-200
                  ${isActive ? "text-ink" : "text-stone hover:text-ink"}
                `}
              >
                {link.label}
              </button>
            );
          })}

          {/* Mobile book CTA */}
          <button
            onClick={() => handleLinkClick("#contact")}
            className="mt-3 w-full py-3 border border-earth text-earth font-jost font-light text-[0.72rem] tracking-[0.18em] uppercase hover:bg-earth hover:text-cream transition-all duration-300"
          >
            Book a session
          </button>
        </nav>
      </div>
    </header>
  );
};
